import React from 'react';
import { block } from '../../utils/cn';
import { useCloseOnTimeout } from '../../utils/useCloseOnTimeout';
import { Icon } from '../../Icon';
import { Button } from '../../Button';
import { Alarm, CrossIcon, Info, Success } from '../../icons';
import i18n from '../i18n';
import './Toast.css';
const b = block('toast');
const FADE_IN_LAST_ANIMATION_NAME = 'toast-display-end';
const FADE_OUT_LAST_ANIMATION_NAME = 'toast-hide-end';
const DEFAULT_TIMEOUT = 5000;
const CROSS_ICON_SIZE = 12;
const TITLE_ICONS = {
    info: Info,
    success: Success,
    warning: Alarm,
    error: Alarm,
};
var ToastStatus;
(function (ToastStatus) {
    ToastStatus["Creating"] = "creating";
    ToastStatus["ShowingIndents"] = "showing-indents";
    ToastStatus["ShowingHeight"] = "showing-height";
    ToastStatus["Hiding"] = "hiding";
    ToastStatus["Shown"] = "shown";
})(ToastStatus || (ToastStatus = {}));
function getHeight(ref) {
    var _a;
    return (_a = ref.current) === null || _a === void 0 ? void 0 : _a.offsetHeight;
}
function useToastHeight({ isOverride, status }) {
    const [height, setHeight] = React.useState(undefined);
    const ref = React.useRef(null);
    const heightRef = React.useRef();
    React.useEffect(() => {
        // ATTENTION: getting `offsetHeight` is important for correct transaction of `height`
        // HOW THIS WORKS:
        // On the step of changing state to `ShowingIndent` we set class with height `transition`
        // We need now to apply this styles, to achieve this we're calling `offsetHeight`, to force repaint
        // Now we call changing state to `ShowingHeight` and changing height now happen with transition
        if (status === ToastStatus.ShowingIndents) {
            heightRef.current = getHeight(ref);
        }
    }, [status]);
    React.useEffect(() => {
        const height = typeof heightRef.current === 'number' && !isOverride
            ? heightRef.current
            : getHeight(ref);
        setHeight(height);
    }, [isOverride]);
    const style = {};
    if (height && status !== ToastStatus.ShowingIndents && status !== ToastStatus.Shown) {
        style.height = height;
    }
    return { style, ref };
}
function useToastStatus({ onRemove }) {
    const [status, setStatus] = React.useState(ToastStatus.Creating);
    React.useEffect(() => {
        if (status === ToastStatus.Creating) {
            setStatus(ToastStatus.ShowingIndents);
        }
        else if (status === ToastStatus.ShowingIndents) {
            setStatus(ToastStatus.ShowingHeight);
        }
    }, [status]);
    const onFadeInAnimationEnd = (e) => {
        if (e.animationName === FADE_IN_LAST_ANIMATION_NAME) {
            setStatus(ToastStatus.Shown);
        }
    };
    const onFadeOutAnimationEnd = (e) => {
        if (e.animationName === FADE_OUT_LAST_ANIMATION_NAME) {
            onRemove();
        }
    };
    let onAnimationEnd;
    if (status === ToastStatus.ShowingHeight) {
        onAnimationEnd = onFadeInAnimationEnd;
    }
    if (status === ToastStatus.Hiding) {
        onAnimationEnd = onFadeOutAnimationEnd;
    }
    const handleClose = React.useCallback(() => {
        setStatus(ToastStatus.Hiding);
    }, []);
    return { status, containerProps: { onAnimationEnd }, handleClose };
}
function renderActions({ actions, onClose }) {
    if (!actions) {
        return null;
    }
    return (React.createElement("div", { className: b('actions') }, actions.map(({ label, onClick, view = 'outlined', removeAfterClick = true }, index) => {
        const onActionClick = () => {
            onClick();
            if (removeAfterClick) {
                onClose();
            }
        };
        return (React.createElement(Button, { key: `${label}__${index}`, className: b('action'), onClick: onActionClick, type: "button", view: view }, label));
    })));
}
function renderIcon({ type }) {
    if (!type) {
        return null;
    }
    return React.createElement(Icon, { data: TITLE_ICONS[type], className: b('icon', { [type]: true }) });
}
export function Toast(props) {
    const { content, actions, title, className, type, allowAutoHiding = true, isClosable = true, isOverride = false, mobile = false, } = props;
    const { status, containerProps: statusProps, handleClose, } = useToastStatus({ onRemove: props.removeCallback });
    const heightProps = useToastHeight({ isOverride, status });
    const timeout = allowAutoHiding ? props.timeout || DEFAULT_TIMEOUT : undefined;
    const closeOnTimeoutProps = useCloseOnTimeout({ onClose: handleClose, timeout });
    const mods = {
        mobile,
        appearing: status === ToastStatus.ShowingIndents || status === ToastStatus.ShowingHeight,
        'show-animation': status === ToastStatus.ShowingHeight,
        'hide-animation': status === ToastStatus.Hiding,
        created: status !== ToastStatus.Creating,
        [type || 'default']: true,
    };
    return (React.createElement("div", Object.assign({ className: b(mods, className) }, statusProps, heightProps, closeOnTimeoutProps),
        React.createElement("div", { className: b('container') },
            renderIcon({ type }),
            React.createElement("h3", { className: b('title') }, title),
            isClosable && (React.createElement(Button, { view: "flat-secondary", className: b('btn-close'), onClick: handleClose, extraProps: { 'aria-label': i18n('label_close-button') } },
                React.createElement(Icon, { data: CrossIcon, size: CROSS_ICON_SIZE }))),
            Boolean(content) && React.createElement("div", { className: b('content') }, content),
            renderActions({ actions, onClose: handleClose }))));
}
